import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { getWeekRangeFromDate, isValidDate } from "../helpers";
import { DATE_TIME_FORMAT } from "../constants";
const generateTimesheetExcel = (data) => {
  const workbook = XLSX.utils.book_new();
  let projectIds = [...new Set(data.map((project) => project.projectId))].join(
    "_"
  );
  let fileName = `Timesheet_${projectIds}.xlsx`;

  data.forEach((project) => {
    const sheetData = [];
    sheetData.push(["Project ID", project.projectId]);
    sheetData.push(["Manager Name", project.managerName]);
    sheetData.push(["Manager Email", project.managerEmail]);
    sheetData.push(["Timesheet ID", project.timesheetId]);
    sheetData.push(["Status", project.status]);
    sheetData.push(["Employee Comments", project.comments || "N/A"]);
    sheetData.push(["Approver Comments", project.approverComments || "N/A"]);
    sheetData.push([]);
    const uniqueDates = [
      ...new Set(
        project.timesheetData.flatMap((task) =>
          task.hours.map((entry) => entry.comparator)
        )
      ),
    ].sort();

    const headerRow = [
      "Task Name",
      "Pay Code",
      "Billable Type",
      ...uniqueDates,
      "Total Hours",
    ];

    sheetData.push(headerRow);

    let columnTotals = new Array(uniqueDates.length).fill(0);
    let grandTotal = 0;

    project.timesheetData.forEach((task) => {
      let row = [task.projectTaskName, task.payCode, task.billableType];

      let rowTotal = 0;
      uniqueDates.forEach((date, index) => {
        const entry = task.hours.find((h) => h.comparator === date);
        const hours = entry ? entry.hours : 0;
        row.push(hours);
        rowTotal += hours;
        columnTotals[index] += hours;
      });

      row.push(rowTotal);
      grandTotal += rowTotal;
      sheetData.push(row);
    });

    sheetData.push(["", "", "Total per Day", ...columnTotals, grandTotal]);

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({
        r: sheetData.length - 1,
        c: C,
      });
      if (!worksheet[cellAddress]) continue;
      worksheet[cellAddress].s = { font: { bold: true } };
    }
    headerRow.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: sheetData.length - uniqueDates.length - 2,
        c: colIndex,
      });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s = { font: { bold: true } };
      }
    });
    worksheet["!cols"] = [
      { wch: 20 },
      { wch: 15 },
      { wch: 20 },
      ...uniqueDates.map(() => ({ wch: 12 })),
      { wch: 15 },
    ];

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      `Project_${project.projectId}${project.week_key ? project.week_key : ""}`
    );
  });

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const dataBlob = new Blob([excelBuffer], {
    type: "application/octet-stream",
  });

  saveAs(dataBlob, fileName);
};

const generateWeeklyReportExcel = (data, identifier) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  worksheet["!cols"] = [{ wch: 15 }, { wch: 20 }, { wch: 12 }, { wch: 8 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Timesheet Report");
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const dataBlob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(dataBlob, `Report_${identifier}.xlsx`);
};
const createWorkBook = (worksheet, worksheetName) => {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, worksheetName);
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const dataBlob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  return dataBlob;
};
const generateProjectsExcelReport = (data) => {
  const formattedExcelData = data.map((prj) => {
    const objVals = {};
    for (let key in prj) {
      if (prj[key]) {
        objVals[key] = prj[key];
      } else {
        objVals[key] = "N/A";
      }
    }
    delete objVals.TotalRecords;
    return objVals;
  });
  const worksheet = XLSX.utils.json_to_sheet(formattedExcelData);
  const cols = Object.keys(formattedExcelData?.[0] || {}).map(() => {
    return { wch: 20 };
  });

  worksheet["!cols"] = cols;
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Project List Report");
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const dataBlob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(dataBlob, `Projects_Report.xlsx`);
};
const generateProjectsExcelTestReport = (data) => {
  const formattedExcelData = data.map((prj) => {
    const {
      ProjectID,
      ProjectName,
      ResourceName,
      Client,
      Address,
      Vendor,
      POStartDate,
      POEndDate,
      PaymentTerms,
    } = prj;
    return {
      "Project ID": ProjectID,
      "Project Name": ProjectName,
      Resource: ResourceName,
      Client,
      Address,
      "Prime Vendor": Vendor,
      "PO Start Date": POStartDate,
      "PO End Date": POEndDate,
      "Payment Terms": PaymentTerms,
    };
  });
  const worksheet = XLSX.utils.json_to_sheet(formattedExcelData);
  worksheet["!cols"] = [
    { wch: 20 },
    { wch: 20 },
    { wch: 20 },
    { wch: 15 },
    { wch: 20 },
    { wch: 20 },
    { wch: 20 },
    { wch: 20 },
    { wch: 20 },
  ];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Project List Report");
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const dataBlob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(dataBlob, `Projects_Report.xlsx`);
};

const generateHolidaysReport = (data, year) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  worksheet["!cols"] = [{ wch: 20 }, { wch: 20 }, { wch: 20 }];
  const dataBlob = createWorkBook(worksheet, "Holidays_report");
  saveAs(dataBlob, `Holidays_Report-${year}.xlsx`);
};
const generateNotificationsReport = (data) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  worksheet["!cols"] = [
    { wch: 20 },
    { wch: 20 },
    { wch: 60 },
    { wch: 20 },
    { wch: 20 },
  ];
  const dataBlob = createWorkBook(worksheet, "Notifications");
  saveAs(dataBlob, `Notifications.xlsx`);
};
const generateTasksReport = (data) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  worksheet["!cols"] = [{ wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }];
  const dataBlob = createWorkBook(worksheet, "Tasks_report");
  saveAs(dataBlob, `Tasks_report.xlsx`);
};

const exportProjectTestPdf = (data) => {
  const doc = new jsPDF();
  doc.setFontSize(12);
  doc.text("Project Report", 14, 15);
  const headers = [[...new Set(...data.map((dt) => Object.keys(dt)))]];
  const formattedTabularData = data.map((dt) => {
    const modifiedObject = { ...dt };
    for (let key in modifiedObject) {
      if (!modifiedObject[key]) {
        modifiedObject[key] = "N/A";
      }
    }
    return Object.values(modifiedObject);
  });

  doc.autoTable({
    head: headers,
    body: formattedTabularData,
    startY: 25,
    theme: "striped",
    styles: { fontSize: 8, cellPadding: 1, overflow: "linebreak" },
    headStyles: {
      fillColor: [100, 100, 100],
      fontSize: 8,
      textColor: [255, 255, 255],
    },
    columnStyles: {
      0: { cellWidth: 12 },
      1: { cellWidth: 20 },
      2: { cellWidth: 20 },
      3: { cellWidth: 20 },
      4: { cellWidth: 20 },
      5: { cellWidth: 20 },
      6: { cellWidth: 18 },
      7: { cellWidth: 18 },
      8: { cellWidth: 22 },
    },
    tableWidth: "wrap",
    margin: { left: 10, right: 10 },
  });

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(6);
    doc.text(
      `Page ${i} of ${totalPages}`,
      doc.internal.pageSize.width - 20,
      doc.internal.pageSize.height - 10
    );
  }

  doc.save("Project_Report.pdf");
};
const exportProjectPdfData = (data) => {
  const doc = new jsPDF();
  doc.setFontSize(12);
  doc.text("Project Report", 14, 15);

  const headers = [
    [
      "Project ID",
      "Project Name",
      "Resource",
      "Client",
      "Address",
      "Prime Vendor",
      "PO Start date",
      "PO End date",
      "Payment Terms",
    ],
  ];

  const tableData = data.map((item) => [
    item.ProjectID,
    item.ProjectName,
    item.ResourceName,
    item.ClientName || "N/A",
    item.ClientAddress || "N/A",
    item.VendorName || "N/A",
    item.POStartDate
      ? moment(item.POStartDate).format(DATE_TIME_FORMAT.DISPLAY)
      : "N/A",
    item.POEndDate
      ? moment(item.POEndDate).format(DATE_TIME_FORMAT.DISPLAY)
      : "N/A",
    item.PaymentTerms || "N/A",
  ]);

  doc.autoTable({
    head: headers,
    body: tableData,
    startY: 25,
    theme: "striped",
    styles: { fontSize: 8, cellPadding: 1, overflow: "linebreak" },
    headStyles: {
      fillColor: [100, 100, 100],
      fontSize: 8,
      textColor: [255, 255, 255],
    },
    columnStyles: {
      0: { cellWidth: 12 },
      1: { cellWidth: 20 },
      2: { cellWidth: 20 },
      3: { cellWidth: 20 },
      4: { cellWidth: 20 },
      5: { cellWidth: 20 },
      6: { cellWidth: 18 },
      7: { cellWidth: 18 },
      8: { cellWidth: 22 },
    },
    tableWidth: "wrap",
    margin: { left: 10, right: 10 },
  });

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(6);
    doc.text(
      `Page ${i} of ${totalPages}`,
      doc.internal.pageSize.width - 20,
      doc.internal.pageSize.height - 10
    );
  }

  doc.save("Project_Report.pdf");
};

const exportTimesheetToPDF = (projects) => {
  const doc = new jsPDF();
  const projectArray = Array.isArray(projects) ? projects : [projects];
  projectArray.forEach((project, index) => {
    let weekString = "Week:";
    const {
      status,
      projectName,
      resourceName,
      clientName,
      projectId,
      managerName,
      workLocation,
      lastApprovedOn,
      lastSubmittedOn,
      poStartDate,
      poEndDate,
    } = project;
    let projectDuration = "N/A";
    if (isValidDate(poStartDate) && isValidDate(poEndDate)) {
      projectDuration = `${moment(poStartDate).format("MM-DD-YYYY")} - ${moment(
        poEndDate
      ).format("MM-DD-YYYY")}`;
    }
    if (index > 0) doc.addPage();
    const uniqueDates = [
      ...new Set(
        project.timesheetData.flatMap((task) =>
          task.hours.map((entry) => entry.comparator)
        )
      ),
    ].sort();
    if (uniqueDates.length) {
      const { firstDay, lastDay } = getWeekRangeFromDate(uniqueDates[0]);
      weekString = `${moment(firstDay).format("MMM DD")} - ${moment(
        lastDay
      ).format("MMM DD, YYYY")}`;
    }
    let grandTotal = 0;
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(`${resourceName} Timesheet for ${weekString}`, 14, 15);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Status: ${status} | Client: ${clientName}`, 14, 25);
    doc.text(
      `Submitted Date:  ${
        isValidDate(lastSubmittedOn)
          ? moment(lastSubmittedOn).format("MMM DD YYYY, h:mm A")
          : "N/A"
      }`,
      14,
      35
    );
    doc.text(
      `Approved Date: ${
        isValidDate(lastApprovedOn)
          ? moment(lastApprovedOn).format("MMM DD YYYY, h:mm A")
          : "N/A"
      }`,
      14,
      42
    );
    doc.setFont("helvetica", "bold");
    const assignmentHeaders = [
      [
        "Assignment ID",
        "Assignment Project",
        "Date Range",
        "Manager Name",
        "Tax Work Location",
      ],
    ];
    const assignmentData = [
      [
        projectId,
        projectName,
        projectDuration,
        managerName || "N/A",
        workLocation || "N/A",
      ],
    ];

    doc.autoTable({
      head: assignmentHeaders,
      body: assignmentData,
      startY: 58,
      styles: { fontSize: 9, cellPadding: 3, textColor: [0, 0, 0] },
      headStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0] },
    });

    const workHeaders = [["Date", "Hours", "Project Task", "Pay Code"]];
    const workData = [];
    const workDataModel = [];

    project.timesheetData.forEach((task) => {
      let rowTotal = 0;
      uniqueDates.forEach((date) => {
        const entry = task.hours.find((h) => h.comparator === date);
        const hours = entry ? entry.hours : 0;
        rowTotal += hours;
        workDataModel.push({
          comparator: entry.comparator,
          hours: hours,
          taskName: task.projectTaskName,
          billableType: task.billableType,
          payCode: task.payCode,
        });
      });
      grandTotal += rowTotal;
    });

    const reducedWorkData = workDataModel.reduce((rv, cv) => {
      const prevIndex = rv.findIndex(
        (entry) => entry.comparator === cv.comparator
      );
      if (prevIndex > -1) {
        const prevRow = rv[prevIndex];
        if (prevRow.taskName === cv.taskName && cv.hours) {
          rv[prevIndex].hours += cv.hours;
          rv[prevIndex].billables.push(cv.billableType);
          rv[prevIndex].payCodes.push(cv.payCode);
        } else {
          if (cv.hours) {
            rv.push({
              ...cv,
              billables: [cv.billableType],
              payCodes: [cv.payCode],
            });
          }
        }
      } else {
        rv.push({
          ...cv,
          billables: [cv.billableType],
          payCodes: [cv.payCode],
        });
      }
      return rv;
    }, []);

    reducedWorkData.sort((left, right) => {
      return moment
        .utc(new Date(left.comparator))
        .diff(moment.utc(new Date(right.comparator)));
    });

    reducedWorkData.forEach((data) => {
      workData.push([
        moment(data.comparator).format("MM-DD-YYYY"),
        data.hours.toFixed(2),
        `${data.taskName} (${[...new Set(data.billables)].join(",")})`,
        `${[...new Set(data.payCodes)].join(",")}`,
      ]);
    });

    doc.autoTable({
      head: workHeaders,
      body: workData,
      startY: doc.lastAutoTable.finalY + 10,
      styles: { fontSize: 9, cellPadding: 3, textColor: [0, 0, 0] },
      headStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0] },
    });

    doc.setFontSize(10);
    doc.text(
      `Total: ${grandTotal.toFixed(2)}`,
      14,
      doc.lastAutoTable.finalY + 10
    );
  });

  doc.save("Timesheet_Report.pdf");
};

export {
  generateTimesheetExcel,
  generateWeeklyReportExcel,
  generateProjectsExcelReport,
  generateHolidaysReport,
  generateTasksReport,
  exportTimesheetToPDF,
  exportProjectPdfData,
  exportProjectTestPdf,
  generateProjectsExcelTestReport,
  generateNotificationsReport,
};
